import { useEffect, useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Eye, Image as ImageIcon, MousePointer, Pencil, Plus, Trash2, Video } from 'lucide-react';
import { toast } from 'sonner';

const LEARNER_TYPES = [
  { id: 'free', label: 'Free' },
  { id: 'trial', label: 'Trial' },
  { id: 'elite', label: 'Elite' },
];
const STAGES = [
  { id: 'prenatal', label: 'Prenatal' },
  { id: 'postnatal', label: 'Postnatal' },
];
const PLACEMENTS = [
  { id: 'home_top', label: 'Home (Top)' },
  { id: 'soulai_top', label: 'SoulAI (Top)' },
  { id: 'classes_top', label: 'Classes (Top)' },
  { id: 'course_player', label: 'Course Player' },
];

const blankForm = () => ({
  title: '', subtitle: '',
  media_type: 'image',
  media_url: '',
  bunny_video_id: '',
  thumbnail_url: '',
  placement: 'home_top',
  target_learner_types: [],
  target_pregnancy_stages: [],
  visibility: 'all',
  start_date: '', end_date: '',
  is_active: true,
});

export default function BannerAdmin() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, banner: null });
  const [form, setForm] = useState(blankForm());
  const [uploading, setUploading] = useState(false);

  const fetchBanners = () =>
    api.get('/api/admin/banners').then((res) => setBanners(res.data.banners || []));
  useEffect(() => { fetchBanners().finally(() => setLoading(false)); }, []);

  const toggleArrayItem = (arr, item) =>
    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      if (form.media_type === 'image') {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/api/uploads/image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setForm((f) => ({ ...f, media_url: data.url || data.public_url }));
        toast.success('Image uploaded');
      } else {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/api/uploads/video-banner-file', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setForm((f) => ({ ...f, media_url: data.url || data.public_url, bunny_video_id: '' }));
        toast.success('Video uploaded');
      }
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/uploads/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, thumbnail_url: data.url || data.public_url }));
      toast.success('Custom thumbnail uploaded');
    } catch (err) {
      toast.error(`Thumbnail upload failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.media_url) {
      toast.error('Title and media are required');
      return;
    }
    try {
      const payload = { ...form };
      if (payload.media_type === 'image') {
        delete payload.bunny_video_id;
      }
      if (dialog.banner) {
        await api.put(`/api/admin/banners/${dialog.banner.id || dialog.banner._id}`, payload);
        toast.success('Banner updated');
      } else {
        await api.post('/api/admin/banners', payload);
        toast.success('Banner created');
      }
      setDialog({ open: false, banner: null });
      fetchBanners();
    } catch (err) {
      toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const openEdit = (b) => {
    setForm({
      title: b.title || '',
      subtitle: b.subtitle || '',
      media_type: b.media_type || 'image',
      media_url: b.media_url || b.image_url || '',  // v1 fallback
      bunny_video_id: b.bunny_video_id || '',
      thumbnail_url: b.thumbnail_url || '',
      placement: b.placement || 'home_top',
      target_learner_types: b.target_learner_types || [],
      target_pregnancy_stages: b.target_pregnancy_stages || [],
      start_date: b.start_date || '',
      end_date: b.end_date || '',
      is_active: b.is_active !== false,
    });
    setDialog({ open: true, banner: b });
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete banner "${b.title}"?`)) return;
    try {
      await api.delete(`/api/admin/banners/${b.id || b._id}`);
      toast.success('Deleted');
      fetchBanners();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const audienceLabel = (b) => {
    const lt = b.target_learner_types?.length ? b.target_learner_types.join('/') : 'all';
    const st = b.target_pregnancy_stages?.length ? b.target_pregnancy_stages.join('/') : 'all stages';
    return `${lt} · ${st}`;
  };

  return (
    <div className="space-y-6" data-testid="banner-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Banners</h1>
          <p className="text-muted-foreground mt-1">Carousel banners shown to learners on app + web. Image (Emergent storage) or video (Bunny Stream).</p>
        </div>
        <Button
          data-testid="banner-create-btn"
          onClick={() => { setForm(blankForm()); setDialog({ open: true, banner: null }); }}
        >
          <Plus className="h-4 w-4 mr-2" />Create Banner
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pt-5">
          {loading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : banners.length === 0 ? (
            <p className="text-muted-foreground" data-testid="banner-empty-state">No banners yet. Click "Create Banner" to add one.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Placement</TableHead>
                  <TableHead className="text-right">Stats</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {banners.map((b) => (
                  <TableRow key={b.id || b._id} data-testid={`banner-row-${b.id || b._id}`}>
                    <TableCell>
                      <div className="font-medium">{b.title}</div>
                      {b.subtitle ? <div className="text-xs text-muted-foreground">{b.subtitle}</div> : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant={b.media_type === 'video' ? 'default' : 'secondary'}>
                        {b.media_type === 'video' ? <Video className="h-3 w-3 mr-1" /> : <ImageIcon className="h-3 w-3 mr-1" />}
                        {b.media_type || 'image'}
                      </Badge>
                    </TableCell>
                    <TableCell><span className="text-sm">{audienceLabel(b)}</span></TableCell>
                    <TableCell>{b.placement}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-3 text-sm text-muted-foreground">
                        <span title="Impressions"><Eye className="h-3 w-3 inline" /> {b.impression_count || 0}</span>
                        <span title="Clicks"><MousePointer className="h-3 w-3 inline" /> {b.click_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`banner-edit-${b.id || b._id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(b)} data-testid={`banner-delete-${b.id || b._id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(open) => setDialog({ open, banner: open ? dialog.banner : null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="banner-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.banner ? 'Edit Banner' : 'Create Banner'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>Title *</Label>
              <Input data-testid="banner-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Trial Class Sale" />
            </div>
            <div>
              <Label>Subtitle (optional)</Label>
              <Textarea data-testid="banner-subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} rows={2} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Media type</Label>
                <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v, media_url: '', bunny_video_id: '' })}>
                  <SelectTrigger data-testid="banner-media-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Placement</Label>
                <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
                  <SelectTrigger data-testid="banner-placement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLACEMENTS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Upload {form.media_type}</Label>
              <Input
                type="file"
                accept={form.media_type === 'image' ? 'image/*' : 'video/*'}
                onChange={handleFileUpload}
                disabled={uploading}
                data-testid="banner-file-input"
              />
              {/* V14.5 Part 19z8 — Founder ask (2026-05-22): publish recommended
                  sizes so admins don't upload a 4K poster and tank App load times. */}
              <p className="text-[11px] text-muted-foreground mt-1">
                {form.media_type === 'image'
                  ? 'Recommended: 1080×1080 (square) or 1280×720 (16:9). JPG/PNG/WebP, ≤ 5 MB.'
                  : 'Recommended: 1080×1080 (square) or 1280×720 (16:9) MP4. 10–30 s loop, ≤ 50 MB.'}
              </p>
              {uploading && <p className="text-xs text-muted-foreground mt-1">Uploading…</p>}
              {form.media_url && (
                <div className="mt-2 text-xs text-green-600">
                  ✓ {form.media_type === 'video' ? 'Video uploaded' : 'Image uploaded'}
                </div>
              )}
            </div>

            {/* V14.6 — Optional custom poster for VIDEO banners. Bunny auto-
                generates a poster frame, but its direct thumbnail URL is blocked
                by the library (403). Uploading a custom image gives a reliable,
                public poster shown before the video plays. */}
            {form.media_type === 'video' && (
              <div>
                <Label>Custom thumbnail / poster <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  disabled={uploading}
                  data-testid="banner-thumbnail-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Leave blank to use Bunny's auto-generated poster. Recommended 1280×720 JPG/PNG, ≤ 2 MB.
                </p>
                {form.thumbnail_url && <div className="mt-2 text-xs text-green-600">✓ Custom thumbnail set</div>}
              </div>
            )}

            <div>
              <Label className="mb-2 block">Target Learner Types <span className="text-xs text-muted-foreground">(empty = all · multiple allowed)</span></Label>
              <div className="flex gap-4">
                {LEARNER_TYPES.map((lt) => (
                  <label key={lt.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.target_learner_types.includes(lt.id)}
                      onCheckedChange={() =>
                        setForm({ ...form, target_learner_types: toggleArrayItem(form.target_learner_types, lt.id) })
                      }
                      data-testid={`banner-target-lt-${lt.id}`}
                    />
                    {lt.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Target Pregnancy Stages <span className="text-xs text-muted-foreground">(empty = all · multiple allowed)</span></Label>
              <div className="flex gap-4">
                {STAGES.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.target_pregnancy_stages.includes(s.id)}
                      onCheckedChange={() =>
                        setForm({ ...form, target_pregnancy_stages: toggleArrayItem(form.target_pregnancy_stages, s.id) })
                      }
                      data-testid={`banner-target-stage-${s.id}`}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Visibility</Label>
              <div className="flex gap-4">
                {[
                  { id: 'all', label: 'App + Web' },
                  { id: 'app', label: 'App only' },
                  { id: 'web', label: 'Web only' },
                ].map((v) => (
                  <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="visibility"
                      checked={form.visibility === v.id}
                      onChange={() => setForm({ ...form, visibility: v.id })}
                      className="accent-rose-600"
                      data-testid={`banner-visibility-${v.id}`}
                    />
                    {v.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="banner-start-date" />
                <p className="text-[11px] text-muted-foreground mt-1">Leave blank = starts immediately.</p>
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="banner-end-date" />
                <p className="text-[11px] text-muted-foreground mt-1">Leave blank = no end date — stays active until you turn it off or delete it.</p>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="banner-is-active" />
              Active (visible to learners now)
            </label>
            <p className="text-[11px] text-muted-foreground -mt-2 ml-6">
              If unchecked, this banner is saved as a draft and is <strong>not shown to learners</strong> on web or app — turn it on when ready.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, banner: null })}>Cancel</Button>
            <Button onClick={handleSave} data-testid="banner-save-btn" disabled={uploading}>
              {dialog.banner ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
